import React, { useState, CSSProperties, useEffect } from 'react';
import { Dropdown, Icon } from 'semantic-ui-react';
import agent from '../../../../app/api/agent';
import { v4 as uuid } from 'uuid';
import { toast } from 'react-toastify';
import { observer } from 'mobx-react-lite';

// ============ INTERFACES ============
interface ProjectItem {
  id: string;
  name: string;
  value: string;
}

interface DropdownOption {
  key: string;
  text: string;
  value: string;
}

// API Payload Structure
interface TaskAllocationPayload {
  id: string;
  projectId: string;
  projectName: string;
  teamName: string;
  userId: string;
  startTime: string;
  endTime: string;
  createdAt: string;
}

interface NFAITimeSheetTaskAllocationProps {
  titleLoader: boolean;
  projectList: ProjectItem[];
}

// ============ TEAM OPTIONS ============
const TEAM_OPTIONS: DropdownOption[] = [
  { key: 'team-A', text: 'Team A', value: 'Team A' },
  { key: 'team-B', text: 'Team B', value: 'Team B' },
  { key: 'team-C', text: 'Team C', value: 'Team C' },
  { key: 'team-D', text: 'Team D', value: 'Team D' },
];

// ============ STYLES ============
const styles: { [key: string]: CSSProperties } = {
  container: {
    background: '#fff',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    width: '90vw',
    maxWidth: '900px',
    margin: 'auto',
    marginTop: '30px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  headerIcon: {
    width: '40px',
    height: '40px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#1e293b',
  },
  headerSubtitle: {
    margin: 0,
    color: '#94a3b8',
    fontSize: '13px',
  },
  resetBtn: {
    background: 'transparent',
    border: '1px solid #e2e8f0',
    color: '#64748b',
    fontSize: '13px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
  },
  // Progress Bar
  progressContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '32px',
    position: 'relative' as const,
  },
  progressLine: {
    position: 'absolute' as const,
    top: '16px',
    left: '20%',
    right: '20%',
    height: '3px',
    background: '#e2e8f0',
    zIndex: 0,
    borderRadius: '2px',
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #3b82f6, #10b981)',
    borderRadius: '2px',
    transition: 'width 0.4s ease',
  },
  stepsWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '70%',
    position: 'relative' as const,
    zIndex: 1,
  },
  step: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '8px',
  },
  stepCircle: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 600,
    background: '#fff',
    border: '3px solid #e2e8f0',
    color: '#94a3b8',
    transition: 'all 0.3s ease',
  },
  stepCircleActive: {
    borderColor: '#3b82f6',
    color: '#3b82f6',
    boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.15)',
  },
  stepCircleCompleted: {
    background: 'linear-gradient(135deg, #3b82f6, #10b981)',
    borderColor: 'transparent',
    color: '#fff',
  },
  stepLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#94a3b8',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  stepLabelActive: {
    color: '#3b82f6',
  },
  // Form Styles
  formContainer: {
    marginBottom: '24px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    marginBottom: '20px',
  },
  formRowFull: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '20px',
    marginBottom: '20px',
  },
  formRowlabel: {
    margin: '0px 10px',
  },
  formField: {
    background: '#f8fafc',
    borderRadius: '10px',
    padding: '16px',
    border: '2px solid #e2e8f0',
    transition: 'all 0.3s ease',
    position: 'relative' as const,
  },
  formFieldActive: {
    borderColor: '#3b82f6',
    background: '#f0f9ff',
    boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.1)',
  },
  formFieldCompleted: {
    borderColor: '#10b981',
    background: '#f0fdf4',
  },
  formFieldDisabled: {
    opacity: 0.5,
    pointerEvents: 'none' as const,
  },
  fieldLabel: {
    marginBottom: '12px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
  },
  fieldLabelCentered: {
    margin: '0 10px',
  },
  required: {
    color: '#ef4444',
  },
  badge: {
    background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
    color: '#fff',
    fontSize: '11px',
    padding: '2px 8px',
    borderRadius: '12px',
    marginLeft: '5px',
    fontWeight: 600,
  },
  successText: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '10px',
    color: '#10b981',
    fontSize: '12px',
    fontWeight: 500,
  },
  dropdown: {
    width: '100%',
  },
  // Action Buttons
  actionRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingTop: '20px',
    borderTop: '1px solid #e2e8f0',
  },
  submitButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: 'none',
    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
    color: '#fff',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
  },
  submitButtonDisabled: {
    background: '#cbd5e1',
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
  // Error Message
  errorMessage: {
    color: '#ef4444',
    fontSize: '12px',
    marginTop: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  // Preview Section
  previewSection: {
    background: '#f8fafc',
    borderRadius: '10px',
    padding: '16px',
    marginBottom: '20px',
    border: '1px solid #e2e8f0',
  },
  previewTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1e293b',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  previewItem: {
    background: '#fff',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '8px',
    border: '1px solid #e2e8f0',
  },
  previewProjectName: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#3b82f6',
    marginBottom: '8px',
  },
  previewDetail: {
    fontSize: '12px',
    color: '#64748b',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '4px',
  },
};

// ============ COMPONENT ============
const NFAITimeSheetTaskAllocation: React.FC<
  NFAITimeSheetTaskAllocationProps
> = ({ titleLoader, projectList }) => {
  // Form State
  const [selectedTitles, setSelectedTitles] = useState<string[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [userOptions, setUserOptions] = useState<DropdownOption[]>([]);
  const [userRawData, setUserRawData] = useState<any[]>([]);

  // Loading & Submission States
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    title?: string;
    team?: string;
    users?: string;
  }>({});

  // Fetch users on mount
  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    userCheck();
    //eslint-disable-next-line
  }, [selectedTitles.length, selectedTeam]);

  const userCheck = async () => {
    if (selectedTitles.length > 0 && selectedTeam) {
      try {
        setIsLoading(true);
        const payload = selectedTitles.map((titleId) => ({
          projectId: titleId,
          team: selectedTeam,
        }));
        const { data } =
          await agent.NFAIRestoTimeSheet.NFAIMasteringTeamUserList(payload);
        const userIds = data.map((item: any) => item.emp_id);
        setSelectedUsers(userIds);
      } catch (error) {
        console.error('Error fetching users for team:', error);
        toast.error('Failed to load users for the selected team');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const fetchUsers = async () => {
    try {
      const getMasteringUsers = (users: any) =>
        users.filter(
          (u: any) => u.departmentName?.trim().toLowerCase() === 'mastering',
        );

      setIsLoading(true);
      const users = await agent.Account.list();
      const filterData = getMasteringUsers(users);
      setUserRawData(filterData);
      setUserOptions(
        filterData.map((u: any) => ({
          key: u.id,
          text: u.displayName,
          value: u.id,
        })),
      );
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  // Get step status
  const getStepStatus = (step: number): 'active' | 'completed' | 'pending' => {
    const checks = [
      selectedTitles.length > 0,
      !!selectedTeam,
      selectedUsers.length > 0,
    ];

    if (checks[step - 1]) return 'completed';

    if (step === 1) return 'active';
    if (step === 2 && checks[0]) return 'active';
    if (step === 3 && checks[0] && checks[1]) return 'active';

    return 'pending';
  };

  // Calculate progress percentage
  const getProgressPercentage = (): number => {
    let completed = 0;
    if (selectedTitles.length > 0) completed++;
    if (selectedTeam) completed++;
    if (selectedUsers.length > 0) completed++;
    return (completed / 3) * 100;
  };

  // Get field style based on status
  const getFieldStyle = (step: number): CSSProperties => {
    const status = getStepStatus(step);
    return {
      ...styles.formField,
      ...(status === 'active' ? styles.formFieldActive : {}),
      ...(status === 'completed' ? styles.formFieldCompleted : {}),
      ...(status === 'pending' && step > 1 ? styles.formFieldDisabled : {}),
    };
  };

  const userHandler = async (_e: any, { value }: any) => {
    const notIncludedData = selectedUsers.filter((obj: string) => !value.includes(obj));
    if(notIncludedData.length > 0){
      notIncludedData.flatMap(user =>
      selectedTitles.map(async (titleId) => {
        try {
          const { data } = await agent.NFAIRestoTimeSheet.NFAIMasteringTeamUserListDelete(titleId, user);
          if (data !== "Something Went Wrong") {
            toast.success(`${userRawData.find(x => x.id === user).displayName} User Removed Successfully!!!`);
          }
          return null;
        } catch (error) {
          return { error: `Failed to delete user ${user} from title ${titleId}`, titleId };
        }
      })
  );
    }
    setSelectedUsers(value as string[]);
    if (errors.users) setErrors({ ...errors, users: undefined });
  };
  // Validate form
  const validateForm = (): boolean => {
    const newErrors: { title?: string; team?: string; users?: string } = {};

    if (selectedTitles.length === 0) {
      newErrors.title = 'Please select at least one project title';
    }

    if (!selectedTeam) {
      newErrors.team = 'Please select a team';
    }

    if (selectedUsers.length === 0) {
      newErrors.users = 'Please select at least one user';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle Submit - Create array of objects (one per project title)
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);

      const team = TEAM_OPTIONS.find((t) => t.value === selectedTeam);

      // Create comma-separated userId string

      // Create array - one object per selected project title
      const payload: TaskAllocationPayload[] = [];

      selectedTitles.forEach((projectId) => {
        const project = projectList.find((p) => p.id === projectId);

        selectedUsers.forEach((userId) => {
          payload.push({
            id: uuid(),
            projectId: project?.id || '',
            projectName: project?.name || '',
            teamName: team?.text || '',
            userId: userId,
            startTime: new Date().toISOString(),
            endTime: new Date().toISOString(),
            createdAt: new Date().toISOString(),
          });
        });
      });

      await agent.NFAIRestoTimeSheet.NFAIAllocatedMasteringSubtask(payload);

      // Call API
      // await agent.NFAIRestoTimeSheet.NFAIAllocatedMasteringSubtask(payload);

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      toast.success(`Task allocation created successfully!`);

      // Reset form
      handleReset();
    } catch (error) {
      console.error('Error submitting:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setSelectedTitles([]);
    setSelectedTeam('');
    setSelectedUsers([]);
    setErrors({});
  };

  // Check if form is valid
  const isFormValid =
    selectedTitles.length > 0 && selectedTeam && selectedUsers.length > 0;

  const stepLabels = ['TITLES', 'TEAM', 'USERS'];

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.headerIcon}>
            <Icon
              name="tasks"
              style={{ color: '#fff', margin: 0, fontSize: '18px' }}
            />
          </div>
          <div>
            <h3 style={styles.headerTitle}>Task Allocation</h3>
            <p style={styles.headerSubtitle}>
              Assign tasks and subtasks to team members
            </p>
          </div>
        </div>
        {(selectedTitles.length > 0 ||
          selectedTeam ||
          selectedUsers.length > 0) && (
          <button style={styles.resetBtn} onClick={handleReset}>
            <Icon name="refresh" style={{ margin: 0, fontSize: '12px' }} />
            Reset
          </button>
        )}
      </div>

      {/* Progress Bar */}
      <div style={styles.progressContainer}>
        <div style={styles.progressLine}>
          <div
            style={{
              ...styles.progressFill,
              width: `${getProgressPercentage()}%`,
            }}
          />
        </div>
        <div style={styles.stepsWrapper}>
          {[1, 2, 3].map((step) => {
            const status = getStepStatus(step);
            return (
              <div key={step} style={styles.step}>
                <div
                  style={{
                    ...styles.stepCircle,
                    ...(status === 'active' ? styles.stepCircleActive : {}),
                    ...(status === 'completed'
                      ? styles.stepCircleCompleted
                      : {}),
                  }}
                >
                  {status === 'completed' ? (
                    <Icon
                      name="check"
                      style={{ margin: 0, fontSize: '14px' }}
                    />
                  ) : (
                    step
                  )}
                </div>
                <span
                  style={{
                    ...styles.stepLabel,
                    ...(status !== 'pending' ? styles.stepLabelActive : {}),
                  }}
                >
                  {stepLabels[step - 1]}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      {/* Form */}
      <div style={styles.formContainer}>
        {/* Row 1: Project Title (Multi-select) & Team */}
        <div style={styles.formRow}>
          {/* Project Title - Multi-select */}
          <div style={{ ...getFieldStyle(1) }}>
            <label>
              <Icon
                name="film"
                style={{ color: '#3b82f6', margin: 0, fontSize: '14px' }}
              />
              <span style={styles.fieldLabelCentered}>Project Title</span>
              <span style={styles.required}>*</span>
              {selectedTitles.length > 0 && (
                <span style={styles.badge}>{selectedTitles.length}</span>
              )}
            </label>
            <Dropdown
              placeholder="Select Project Titles"
              options={projectList.map((p) => ({
                key: p.id,
                text: p.name,
                value: p.id,
              }))}
              value={selectedTitles}
              onChange={(_e, { value }) => {
                setSelectedTitles(value as string[]);
                if (errors.title) setErrors({ ...errors, title: undefined });
              }}
              selection
              fluid
              multiple
              search
              clearable
              loading={titleLoader}
              error={!!errors.title}
              style={styles.dropdown}
            />
            {selectedTitles.length > 0 && (
              <div style={styles.successText}>
                <Icon
                  name="check circle"
                  style={{ margin: 0, fontSize: '12px' }}
                />
                {selectedTitles.length} title(s) selected
              </div>
            )}
            {errors.title && (
              <div style={styles.errorMessage}>
                <Icon
                  name="exclamation circle"
                  style={{ margin: 0, fontSize: '12px' }}
                />
                {errors.title}
              </div>
            )}
          </div>

          {/* Team */}
          <div style={{ ...getFieldStyle(2) }}>
            <label>
              <Icon
                name="group"
                style={{ color: '#3b82f6', margin: 0, fontSize: '14px' }}
              />
              <span style={styles.fieldLabelCentered}>Team</span>
              <span style={styles.required}>*</span>
            </label>
            <Dropdown
              placeholder="Select Team"
              options={TEAM_OPTIONS}
              value={selectedTeam}
              onChange={async (_e, { value }) => {
                setSelectedTeam(value as string);
                if (errors.team) setErrors({ ...errors, team: undefined });
              }}
              selection
              fluid
              clearable
              disabled={selectedTitles.length === 0}
              error={!!errors.team}
              style={styles.dropdown}
            />
            {selectedTeam && (
              <div style={styles.successText}>
                <Icon
                  name="check circle"
                  style={{ margin: 0, fontSize: '12px' }}
                />
                Team selected
              </div>
            )}
            {errors.team && (
              <div style={styles.errorMessage}>
                <Icon
                  name="exclamation circle"
                  style={{ margin: 0, fontSize: '12px' }}
                />
                {errors.team}
              </div>
            )}
          </div>
        </div>

        {/* Row 2: User List (Full Width) */}
        <div>
          <div style={{ ...getFieldStyle(3) }}>
            <label>
              <Icon
                name="users"
                style={{ color: '#3b82f6', margin: 0, fontSize: '14px' }}
              />
              <span style={styles.fieldLabelCentered}>User List</span>
              <span style={styles.required}>*</span>
              {selectedUsers.length > 0 && (
                <span style={styles.badge}>{selectedUsers.length}</span>
              )}
            </label>
            <Dropdown
              placeholder="Select Users"
              options={userOptions}
              value={selectedUsers}
              onChange={userHandler}
              selection
              fluid
              multiple
              search
              clearable
              disabled={!selectedTeam}
              loading={isLoading}
              error={!!errors.users}
              style={styles.dropdown}
            />
            {errors.users && (
              <div style={styles.errorMessage}>
                <Icon
                  name="exclamation circle"
                  style={{ margin: 0, fontSize: '12px' }}
                />
                {errors.users}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action Row */}
      <div style={styles.actionRow}>
        <button
          style={{
            ...styles.submitButton,
            ...(!isFormValid || isSubmitting
              ? styles.submitButtonDisabled
              : {}),
          }}
          onClick={handleSubmit}
          disabled={!isFormValid || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Icon name="spinner" loading style={{ margin: 0 }} />
              Submitting...
            </>
          ) : (
            <>
              <Icon name="plus" style={{ margin: 0 }} />
              Add Task Allocation
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default observer(NFAITimeSheetTaskAllocation);
